import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";

export function CardCarousel({ slides, testId = "card-carousel", interval = 6000 }) {
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [count, setCount] = useState(0);
  const hovering = useRef(false);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setSelected(api.selectedScrollSnap());
    const onSel = () => setSelected(api.selectedScrollSnap());
    api.on("select", onSel);
    api.on("reInit", onSel);
    return () => { api.off("select", onSel); };
  }, [api]);

  useEffect(() => {
    if (!api) return;
    const id = setInterval(() => { if (!hovering.current) api.scrollNext(); }, interval);
    return () => clearInterval(id);
  }, [api, interval]);

  return (
    <Carousel
      setApi={setApi}
      opts={{ loop: true, align: "center" }}
      onMouseEnter={() => { hovering.current = true; }}
      onMouseLeave={() => { hovering.current = false; }}
      className="relative"
      data-testid={testId}
    >
      <CarouselContent>
        {slides.map((node, i) => (
          <CarouselItem key={i} data-testid={`${testId}-slide-${i}`}>
            <motion.div
              initial={{ opacity: 0.4 }}
              animate={{ opacity: selected === i ? 1 : 0.4 }}
              transition={{ duration: 0.5 }}
            >
              {node}
            </motion.div>
          </CarouselItem>
        ))}
      </CarouselContent>

      <div className="flex items-center justify-center gap-5 mt-9">
        <button
          onClick={() => api?.scrollPrev()}
          data-testid={`${testId}-prev`}
          aria-label="Précédent"
          className="w-10 h-10 rounded-full border border-primary/25 bg-primary/[0.06] text-primary flex items-center justify-center transition-all duration-300 hover:border-primary/60 hover:-translate-x-0.5"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-2.5">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              data-testid={`${testId}-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${selected === i ? "w-8 bg-primary" : "w-2 bg-primary/30 hover:bg-primary/50"}`}
            />
          ))}
        </div>
        <button
          onClick={() => api?.scrollNext()}
          data-testid={`${testId}-next`}
          aria-label="Suivant"
          className="w-10 h-10 rounded-full border border-primary/25 bg-primary/[0.06] text-primary flex items-center justify-center transition-all duration-300 hover:border-primary/60 hover:translate-x-0.5"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </Carousel>
  );
}
